import type { ReactNode } from 'react'
import { createContext, useContext } from 'react'
import type {
  CustomerProfile, ProfilePatch, Plate, PaymentMethod, SavedCard,
  PortalSubscription, PortalPermit, ExtendResult, ExtendQuote,
  CustomerChatMessage, CustomerChatResponse,
} from './types'

/**
 * The data surface the account experience needs. Each host app (the portal
 * today, the consumer checkout next) provides its own implementation binding
 * its API layer — the shared UI never imports a host's api module directly.
 * Signatures mirror the portal's api.ts functions exactly.
 */
export interface AccountClient {
  fetchMe(): Promise<CustomerProfile>
  patchProfile(patch: ProfilePatch): Promise<Omit<CustomerProfile, 'visits'>>
  addPlate(args: { plate: string; nickname?: string; is_primary?: boolean; expires_at?: string }): Promise<{ plate: Plate; plates: Plate[] }>
  removePlate(id: number): Promise<{ plates: Plate[] }>
  fetchPaymentMethod(): Promise<PaymentMethod>
  /** Every saved card, default first. */
  listPaymentMethods?(): Promise<{ methods: SavedCard[]; defaultId: string | null }>
  /** Make one of the customer's own saved cards the default. */
  setDefaultPaymentMethod?(paymentMethodId: string): Promise<{ ok: boolean; defaultId: string }>
  /** Remove a saved card. Refused (409) if it backs an active subscription. */
  removePaymentMethod?(paymentMethodId: string): Promise<{ ok: boolean; removedId: string; defaultId: string | null }>
  /** Name a card. An empty label clears it. */
  setCardLabel?(paymentMethodId: string, label: string): Promise<{ ok: boolean; label: string | null }>
  createBillingSession(): Promise<{ url: string }>
  fetchSubscriptions(): Promise<PortalSubscription[]>
  fetchPermits(): Promise<PortalPermit[]>
  setAutoRenew(id: string, autoRenew: boolean): Promise<PortalSubscription>
  extendSession(args: { txnId: string; additionalMinutes: number; idempotencyKey: string }): Promise<ExtendResult>
  extendQuote(args: { txnId: string; additionalMinutes: number }): Promise<ExtendQuote>
  fetchReceipt(visitId: string): Promise<Blob>
  emailReceipt(visitId: string): Promise<{ ok: true }>
  sendCustomerChat(messages: CustomerChatMessage[], tz: string): Promise<CustomerChatResponse>
  fileRefundRequest(visitId: string, reason: string, requestedAmountUsd?: number): Promise<{ ok: true; refundId: number | null; humanEta: string }>
  getEmail(): string | null
  /**
   * Phone-number change is SMS-verified, not a plain patchProfile: the number
   * doubles as a login identity + the SMS receipt channel. `startPhoneChange`
   * texts a code to the NEW number; `verifyPhoneChange` checks it, persists the
   * change (server also resets the SMS opt-in), and returns a re-minted session
   * token (hand it to onSessionRefreshed) plus the updated profile.
   *
   * `csrf` rides along because the re-mint MOVES the session's `sub` for a
   * phone-first account (`tel:<old>` → `tel:<new>`), and the CSRF token's
   * signature covers `sub` — so the token the host is holding stops verifying
   * at the same instant. The server mints the replacement on this very response
   * (api/src/portal/phone_auth.ts); this carries it back to the host, which is
   * the only path it has. Optional so an older Worker simply yields undefined.
   */
  startPhoneChange(args: { phone: string }): Promise<{ ok: boolean }>
  verifyPhoneChange(args: { phone: string; code: string }): Promise<{
    token: string
    phone: string
    profile: Omit<CustomerProfile, 'visits'>
    csrf?: string
  }>
}

/**
 * Duck-typed replacement for cross-realm `instanceof ApiError`. Each host app
 * ships its OWN ApiError class, so an `instanceof` check against one host's
 * class would be false for an error thrown by the other's api layer. This
 * checks the shape instead — a non-null object with a numeric `status` and a
 * string `message` — so `.status` / `.message` reads afterwards stay type-safe.
 */
export function isApiError(e: unknown): e is { status: number; message: string } {
  return (
    typeof e === 'object' &&
    e !== null &&
    typeof (e as { status?: unknown }).status === 'number' &&
    typeof (e as { message?: unknown }).message === 'string'
  )
}

export interface AccountEnv {
  client: AccountClient
  billingPortalEnabled: boolean
  onFindParking: () => void
  onSignInAgain: () => void
  focusSessionId?: string | null
  /**
   * Called with the re-minted session token after a verified phone-number
   * change (verifyPhoneChange). The host persists it (e.g. localStorage) so
   * subsequent requests keep authenticating — critical for phone-first accounts
   * whose login identity IS the number. Optional: a host whose identity never
   * changes (email magic-link) can ignore it.
   *
   * `csrf` is the replacement CSRF token for the re-minted session — see
   * verifyPhoneChange above for why one is needed. A host that persists the
   * token must persist this too, or the parker's next cookie-authenticated
   * write 403s until something re-mints. Undefined from an older Worker; the
   * host should then CLEAR its stored token rather than keep a stale one.
   */
  onSessionRefreshed?: (token: string, csrf?: string) => void
  /**
   * Renders the host's own add-a-card form.
   *
   * This is a SLOT rather than a component in this package on purpose: taking
   * a card requires Stripe.js/Elements, and putting that dependency here would
   * push it onto every host — including ones that have no reason to accept
   * card details. Keeping the PAN-handling code in the host that already loads
   * Stripe is also what keeps the PCI surface where it can be reasoned about.
   *
   * Undefined ⇒ the host can't take a card, and no "Add card" affordance is
   * shown at all.
   */
  renderAddCard?: (opts: { onAdded: () => void; onCancel: () => void }) => ReactNode
}

export const AccountEnvContext = createContext<AccountEnv | null>(null)

export function useAccountEnv(): AccountEnv {
  const env = useContext(AccountEnvContext)
  if (!env) throw new Error('AccountExperience: AccountEnv not provided')
  return env
}
